import { z } from 'zod/v4';
// Product category
export const ProductCategorySchema = z.enum([
    'court-filing',
    'process-serving',
    'document-retrieval',
    'eservice',
    'skip-trace',
    'courier',
    'research',
    'other',
]);
// Product status
export const ProductStatusSchema = z.enum([
    'active',
    'inactive',
    'discontinued',
]);
// Pricing type for items
export const PricingTypeSchema = z.enum([
    'flat',
    'per-page',
    'per-attempt',
    'hourly',
    'tiered',
    'variable', // Determined at order time
]);
// Pricing item (nested within product)
export const PricingItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    pricingType: PricingTypeSchema,
    basePrice: z.number(),
    // For per-page, per-attempt, hourly
    unitPrice: z.number().optional(),
    // For tiered pricing
    tiers: z.array(z.object({
        minQuantity: z.number(),
        maxQuantity: z.number().optional(),
        price: z.number(),
    })).optional(),
    // Modifiers
    rushMultiplier: z.number().default(1.5), // Applied for rush orders
    sameDayMultiplier: z.number().default(2.0), // Applied for same-day
    // Flags
    taxable: z.boolean().default(true),
    optional: z.boolean().default(false), // Can be added/removed from order
    // Display order
    sortOrder: z.number().default(0),
});
// Product schema - service offerings with pricing
export const ProductSchema = z.object({
    id: z.string().uuid(),
    code: z.string(), // Short code (e.g., "CF-STD", "PS-RSH")
    name: z.string(),
    description: z.string().optional(),
    category: ProductCategorySchema,
    // Pricing items (nested pricing structure)
    pricingItems: z.array(PricingItemSchema).default([]),
    // Default pricing (if no specific items)
    basePrice: z.number().optional(),
    // Turnaround times (in hours)
    standardTurnaround: z.number().optional(),
    rushTurnaround: z.number().optional(),
    sameDayAvailable: z.boolean().default(false),
    // Applicable locations (empty = all)
    applicableLocationIds: z.array(z.string().uuid()).default([]),
    // SLA settings
    slaHours: z.number().optional(),
    slaPriority: z.enum(['standard', 'priority', 'urgent']).default('standard'),
    // Flags
    requiresDocuments: z.boolean().default(true),
    requiresPartyInfo: z.boolean().default(false),
    requiresAddress: z.boolean().default(false),
    vendorDispatch: z.boolean().default(false), // Requires external vendor
    // Notes
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    // Status
    status: ProductStatusSchema.default('active'),
    // Display
    sortOrder: z.number().default(0),
    featured: z.boolean().default(false),
    // Timestamps
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateProductSchema = ProductSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
});
export const UpdateProductSchema = ProductSchema.partial().omit({
    id: true,
    createdAt: true,
});
// Product preview for hover displays
export const ProductPreviewSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    category: ProductCategorySchema,
    basePrice: z.number().optional(),
    pricingItemCount: z.number(),
    standardTurnaround: z.number().optional(),
    status: ProductStatusSchema,
});
